import React, { useMemo } from 'react';
import { Typography, Row, Col } from 'antd';
import { ContentContainer } from '@common/components';
import { Membership } from 'works/legacy/graphql/works-memberships';
import {
  OrganizationUnitType,
  OrganizationBusinessType,
} from '@institutions/graphql';
import { groupBy } from 'lodash';

const { Title } = Typography;

function groupUnitsByBusiness(organizationUnits: OrganizationUnitType[]): {
  organizationBusiness: OrganizationBusinessType;
  organizationUnits: OrganizationUnitType[];
}[] {
  const organizationBusinesses = deduplicate(
    organizationUnits.map(
      ({ department: { organizationBusiness } }) => organizationBusiness
    )
  );

  const indexedUnits = groupBy(
    organizationUnits,
    (unit) => unit.department.organizationBusiness.id
  );

  return organizationBusinesses.map((business) => ({
    organizationBusiness: business,
    organizationUnits: indexedUnits[business.id] || [],
  }));
}

export function MembersUpdateLocationsStepReview({
  members,
  organizationUnits,
}: {
  members: Membership[];
  organizationUnits: OrganizationUnitType[];
}): JSX.Element {
  const plural = (quantity: number) => (quantity > 1 ? 's' : '');
  const memberPlural = plural(members.length);

  const unitsGroupedByBusiness = useMemo(() => {
    return groupUnitsByBusiness(organizationUnits);
  }, [organizationUnits]);

  const locationPlural = plural(unitsGroupedByBusiness.length);

  return (
    <>
      <ContentContainer
        className="members-review-locations-step"
        title="Update Review"
      >
        <>
          <Row gutter={[0, 24]}>
            <Col>
              <Title level={2}>User{memberPlural} to Update</Title>
              <ul className="members-review-locations-step-list sentry-mask">
                {members.map((member) => (
                  <li key={member.id}>
                    {member.firstName} {member.lastName}, {member.roleName}
                  </li>
                ))}
              </ul>
            </Col>
          </Row>
          <Row>
            <Col>
              <Title level={2}>Location{locationPlural} to (+) Add</Title>
              {unitsGroupedByBusiness.map(
                ({ organizationBusiness, organizationUnits }) => (
                  <div key={organizationBusiness.id}>
                    <Title level={3}>
                      {organizationBusiness.name} ({organizationUnits.length}/
                      {organizationBusiness.organizationUnitsActiveCount})
                    </Title>
                    <ul className="members-review-locations-step-list">
                      {organizationUnits.map((unit) => (
                        <li key={unit.id}>{unit.name}</li>
                      ))}
                    </ul>
                  </div>
                )
              )}
            </Col>
          </Row>
        </>
      </ContentContainer>
      <style jsx>{`
        .members-review-locations-step-list li {
          margin-bottom: 4px;
        }
      `}</style>
    </>
  );
}

function deduplicate<T extends { [key: string]: unknown }>(
  array: T[],
  byKey = 'id'
) {
  const ids = array.map((item) => item[byKey]);
  return array.filter((item, index) => !ids.includes(item[byKey], index + 1));
}
